import * as React from 'react';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { SyncIcon, AngleLeftIcon, AngleRightIcon } from '@patternfly/react-icons';
import toast from 'react-hot-toast';
import { AdminDriverSchedule, DAY_ORDER, DAY_ABBR } from '@app/Schedules/types';
import { getAllSchedules, getRunCalendar } from '@app/Schedules/schedules.api';
import { byDay, dayActive, slotLabel, DayDot } from '@app/Schedules/WeekGrid';
import { AdminCampaignRun } from '@app/Campaigns/types';
import { RunStateBadge, ApprovalBadge, Pill } from '@app/Campaigns/badges';
import { colors, radii, shadows } from '@app/components/designTokens';

type Tab = 'availability' | 'calendar';

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '12px 14px',
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.3px',
  color: colors.subtle,
  borderBottom: `1px solid ${colors.border}`,
  whiteSpace: 'nowrap'
};
const td: React.CSSProperties = {
  padding: '12px 14px',
  fontSize: 13.5,
  color: colors.body,
  borderBottom: `1px solid ${colors.border}`,
  verticalAlign: 'middle',
  whiteSpace: 'nowrap'
};
const dayTh: React.CSSProperties = { ...th, textAlign: 'center', padding: '12px 8px' };
const dayTd: React.CSSProperties = { ...td, textAlign: 'center', padding: '12px 8px' };

/** Monday 00:00 (local) of the week containing d. */
function mondayOf(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const offset = (x.getDay() + 6) % 7; // 0 = Monday
  x.setDate(x.getDate() - offset);
  return x;
}

const timeOf = (iso: string | null) =>
  iso ? new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '—';

export const Schedules: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('availability');

  // --- Weekly availability tab ---
  const [rows, setRows] = useState<AdminDriverSchedule[]>([]);
  const [loadingAvail, setLoadingAvail] = useState(true);
  const [availError, setAvailError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const loadAvailability = useCallback(
    async (spinner: boolean) => {
      if (spinner) setLoadingAvail(true);
      try {
        const data = await getAllSchedules({ q: search || undefined, limit: 200 });
        setRows(data);
        setAvailError(null);
      } catch (e: any) {
        setAvailError(e?.message || 'Failed to load schedules');
        if (!spinner) toast.error('Could not refresh schedules');
      } finally {
        if (spinner) setLoadingAvail(false);
      }
    },
    [search]
  );

  useEffect(() => {
    loadAvailability(true);
  }, [loadAvailability]);

  // --- Run calendar tab ---
  const [weekStart, setWeekStart] = useState<Date>(() => mondayOf(new Date()));
  const [runs, setRuns] = useState<AdminCampaignRun[]>([]);
  const [loadingCal, setLoadingCal] = useState(false);
  const [calError, setCalError] = useState<string | null>(null);

  const loadCalendar = useCallback(
    async (spinner: boolean) => {
      if (spinner) setLoadingCal(true);
      const from = new Date(weekStart);
      const to = new Date(weekStart);
      to.setDate(to.getDate() + 7);
      try {
        const data = await getRunCalendar({ from: from.toISOString(), to: to.toISOString() });
        setRuns(data);
        setCalError(null);
      } catch (e: any) {
        setCalError(e?.message || 'Failed to load run calendar');
        if (!spinner) toast.error('Could not refresh calendar');
      } finally {
        if (spinner) setLoadingCal(false);
      }
    },
    [weekStart]
  );

  useEffect(() => {
    if (tab === 'calendar') loadCalendar(true);
  }, [tab, loadCalendar]);

  const styles: { [k: string]: React.CSSProperties } = {
    wrap: { padding: 24 },
    headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 },
    title: { fontSize: 22, fontWeight: 700, color: colors.title, margin: 0 },
    subtle: { fontSize: 13, color: colors.subtle },
    refreshBtn: { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px', border: `1px solid ${colors.border}`, borderRadius: radii.button, background: '#fff', color: colors.body, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
    tabs: { display: 'flex', gap: 4, borderBottom: `1px solid ${colors.border}`, marginBottom: 16 },
    toolbar: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 },
    search: { flex: 1, minWidth: 220, maxWidth: 340, padding: '10px 12px', border: `1px solid ${colors.border}`, borderRadius: radii.input, fontSize: 14, outline: 'none' },
    card: { background: '#fff', borderRadius: radii.card, boxShadow: shadows.softCard, border: `1px solid ${colors.border}`, overflow: 'hidden' },
    navBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, border: `1px solid ${colors.border}`, borderRadius: radii.button, background: '#fff', color: colors.body, cursor: 'pointer' },
    weekLabel: { fontSize: 14, fontWeight: 700, color: colors.title, minWidth: 220, textAlign: 'center' },
    dayHeader: { display: 'flex', alignItems: 'baseline', gap: 10, padding: '14px 16px 8px', borderBottom: `1px solid ${colors.border}` },
    dayName: { fontSize: 14, fontWeight: 700, color: colors.title },
    dayDate: { fontSize: 12.5, color: colors.subtle }
  };

  const tabBtn = (key: Tab, label: string) => {
    const active = tab === key;
    return (
      <button
        onClick={() => setTab(key)}
        style={{ padding: '10px 16px', border: 'none', background: 'transparent', color: active ? colors.accent : colors.muted, fontSize: 14, fontWeight: 600, cursor: 'pointer', borderBottom: `2px solid ${active ? colors.accent : 'transparent'}`, marginBottom: -1 }}
      >
        {label}
      </button>
    );
  };

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  const weekRangeLabel = `${weekStart.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${weekEnd.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  const shiftWeek = (deltaWeeks: number) => {
    const next = new Date(weekStart);
    next.setDate(next.getDate() + deltaWeeks * 7);
    setWeekStart(next);
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.headerRow}>
        <div>
          <h1 style={styles.title}>Schedules</h1>
          <div style={styles.subtle}>
            {tab === 'availability'
              ? loadingAvail
                ? 'Loading…'
                : `${rows.length} drivers`
              : loadingCal
                ? 'Loading…'
                : `${runs.length} booked runs this week`}
          </div>
        </div>
        <button
          style={styles.refreshBtn}
          onClick={() => (tab === 'availability' ? loadAvailability(true) : loadCalendar(true))}
        >
          <SyncIcon className={loadingAvail || loadingCal ? 'logo-bolt-pulse' : undefined} /> Refresh
        </button>
      </div>

      <div style={styles.tabs}>
        {tabBtn('availability', 'Weekly availability')}
        {tabBtn('calendar', 'Run calendar')}
      </div>

      {tab === 'availability' ? (
        <>
          <div style={styles.toolbar}>
            <input style={styles.search} placeholder="Search by name or email…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>

          {availError && (
            <div style={{ background: colors.dangerRing, color: colors.danger, borderRadius: 10, padding: '12px 16px', fontSize: 14, marginBottom: 12 }}>{availError}</div>
          )}

          <div style={styles.card}>
            {loadingAvail ? (
              <div style={{ padding: 48, textAlign: 'center', color: colors.subtle }}>Loading schedules…</div>
            ) : rows.length === 0 ? (
              <div style={{ padding: 48, textAlign: 'center', color: colors.subtle }}>No drivers match this search.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
                  <thead>
                    <tr>
                      <th style={th}>Driver</th>
                      <th style={dayTh}>Avail.</th>
                      {DAY_ORDER.map(day => (
                        <th key={day} style={dayTh}>{DAY_ABBR[day]}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => {
                      const map = byDay(r.schedules);
                      return (
                        <tr
                          key={r.driverProfileId}
                          onClick={() => navigate(`/drivers/${r.driverProfileId}`)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td style={td}>
                            <div style={{ fontWeight: 600 }}>{r.name || `Driver #${r.driverProfileId}`}</div>
                            <div style={{ fontSize: 12, color: colors.subtle }}>{r.email}</div>
                          </td>
                          <td style={dayTd}>
                            {r.available ? <Pill text="ON" tone="green" /> : <Pill text="OFF" tone="gray" />}
                          </td>
                          {DAY_ORDER.map(day => {
                            const d = map[day];
                            return (
                              <td key={day} style={dayTd}>
                                <DayDot on={dayActive(d)} title={`${DAY_ABBR[day]}: ${slotLabel(d)}`} />
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div style={styles.toolbar}>
            <button style={styles.navBtn} onClick={() => shiftWeek(-1)} aria-label="Previous week">
              <AngleLeftIcon />
            </button>
            <span style={styles.weekLabel}>{weekRangeLabel}</span>
            <button style={styles.navBtn} onClick={() => shiftWeek(1)} aria-label="Next week">
              <AngleRightIcon />
            </button>
            <button style={styles.refreshBtn} onClick={() => setWeekStart(mondayOf(new Date()))}>
              This week
            </button>
          </div>

          {calError && (
            <div style={{ background: colors.dangerRing, color: colors.danger, borderRadius: 10, padding: '12px 16px', fontSize: 14, marginBottom: 12 }}>{calError}</div>
          )}

          {loadingCal ? (
            <div style={{ ...styles.card, padding: 48, textAlign: 'center', color: colors.subtle }}>Loading run calendar…</div>
          ) : (
            DAY_ORDER.map((day, i) => {
              const date = new Date(weekStart);
              date.setDate(date.getDate() + i);
              const dayRuns = runs.filter(r => {
                if (!r.scheduledStartAt) return false;
                const ds = new Date(r.scheduledStartAt);
                return ds.toDateString() === date.toDateString();
              });
              return (
                <div key={day} style={{ ...styles.card, marginBottom: 14 }}>
                  <div style={styles.dayHeader}>
                    <span style={styles.dayName}>{DAY_ABBR[day]}</span>
                    <span style={styles.dayDate}>{date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                    <span style={{ ...styles.dayDate, marginLeft: 'auto' }}>{dayRuns.length} run{dayRuns.length === 1 ? '' : 's'}</span>
                  </div>
                  {dayRuns.length === 0 ? (
                    <div style={{ padding: '16px', color: colors.placeholder, fontSize: 13 }}>No booked runs.</div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
                        <thead>
                          <tr>
                            <th style={th}>Time</th>
                            <th style={th}>Driver</th>
                            <th style={th}>Campaign</th>
                            <th style={th}>State</th>
                            <th style={th}>Approval</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dayRuns.map(r => (
                            <tr key={r.runId}>
                              <td style={td}>
                                {timeOf(r.scheduledStartAt)}
                                {r.scheduledEndAt ? `–${timeOf(r.scheduledEndAt)}` : ''}
                              </td>
                              <td style={td}>
                                {r.driverProfileId != null ? (
                                  <span style={{ color: colors.accent, cursor: 'pointer', fontWeight: 600 }} onClick={() => navigate(`/drivers/${r.driverProfileId}`)}>
                                    {r.driverName || `Driver #${r.driverProfileId}`}
                                  </span>
                                ) : (
                                  <span style={{ color: colors.placeholder }}>unassigned</span>
                                )}
                              </td>
                              <td style={td}>
                                {r.campaignId != null ? (
                                  <span style={{ color: colors.accent, cursor: 'pointer', fontWeight: 600 }} onClick={() => navigate(`/campaigns/${r.campaignId}`)}>
                                    {r.campaignName || `Campaign #${r.campaignId}`}
                                  </span>
                                ) : (
                                  r.campaignName || '—'
                                )}
                              </td>
                              <td style={td}>
                                <RunStateBadge state={r.state} />
                              </td>
                              <td style={td}>
                                <ApprovalBadge state={r.approvalState} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </>
      )}
    </div>
  );
};
